import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

import { httpService } from '@/api/v1/http.service'
import { type Semester } from '@/types/semester'
import { findById } from '@/utils/find-by-id'
import { requiredString, semesterAndYearStatus } from '@/utils/zod'

const responseSchema = z.array(
  z.object({
    id: requiredString(),
    name: requiredString(),
    start_date: z.coerce.date(),
    end_date: z.coerce.date(),
    is_active: z.boolean(),
    status: semesterAndYearStatus
  })
)

const parseResponse = (response: z.infer<typeof responseSchema>): Semester[] =>
  response.map(item => ({
    id: item.id,
    name: item.name,
    startDate: item.start_date,
    endDate: item.end_date,
    isActive: item.is_active,
    status: item.status
  }))

export const userAvailabilitySemesterQueryOptions = (props: {
  userId: string
}) => ({
  queryKey: ['panelUsersAvailabilitySemestersList', props],
  staleTime: 60 * 100,
  queryFn: async () => {
    const semestersResponse =
      await httpService.panel.panelUsersAvailabilitySemestersList(
        props.userId,
        {
          page_size: 'max',
          fetchKeys: {
            id: true,
            name: true,
            start_date: true,
            end_date: true,
            status: true,
            is_active: true
          }
        }
      )

    const semesters = parseResponse(
      responseSchema.parse(semestersResponse.results)
    )

    const currentSemesterIndex = semesters?.findIndex(
      semesterItem => semesterItem.isActive
    )
    const currentSemester = semesters[currentSemesterIndex]
    const previousSemester = semesters[currentSemesterIndex - 1]

    return {
      list: semesters,
      options: semesters?.map(semester => ({
        label: semester.name,
        value: semester.id
      })),
      currentSemester: currentSemester || semesters[semesters.length - 1],
      previousSemester,
      getSemester: findById(semesters)
    }
  }
})

export const useUserAvailabilitySemesters = (props: { userId: string }) =>
  useQuery(userAvailabilitySemesterQueryOptions(props))
